import React, { memo } from 'react';
import { formats, validators } from 'investira.sdk';
import { toDate, workingDaysBetween } from 'investira.sdk/lib/utils/dates';
import { Box, Card, Divider, Icon, Stack, Tooltip, Typography } from 'investira.react.components';

/**
 * Formata uma data cadastral do título.
 *
 * @param {string} pValue Data recebida no payload.
 * @returns {string}
 */
function pvFormatDate(pValue) {
  return formats.formatDate(pValue);
}

/**
 * Calcula a quantidade de dias úteis entre a data atual e o vencimento do título.
 *
 * @param {string} pValue Data de vencimento recebida no payload.
 * @returns {number}
 */
function pvFormatDiasUteisAteVencimento(pValue) {
  return workingDaysBetween(new Date(), toDate(pValue));
}

/**
 * Formata uma taxa percentual preservando quatro casas decimais.
 *
 * @param {number|string} pValue Taxa recebida no payload.
 * @returns {string}
 */
function pvFormatTaxa(pValue) {
  return `${formats.formatNumber(pValue, 4, true, false)}%`;
}

/**
 * Renderiza uma linha de características cadastrais do título.
 *
 * @param {Array<Object>} pItems Informações exibidas na linha.
 * @param {number} pColumns Quantidade de colunas da grade.
 * @returns {JSX.Element}
 */
function pvRenderItems(pItems, pColumns = pItems.length) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${pColumns}, minmax(0, 1fr))`
        },
        rowGap: 2
      }}>
      {pItems.map((pItem, pIndex) => {
        const xHasValue = !validators.isNull(pItem.value) && pItem.value !== '';
        const xValue = xHasValue ? pItem.formatValue(pItem.value) : '---';

        return (
          <Stack
            key={pItem.id}
            sx={{
              minWidth: 0,
              position: 'relative',
              pt: {
                xs: pIndex > 0 ? 2 : 0,
                sm: 0
              },
              pl: {
                xs: 0,
                sm: pIndex > 0 ? 2 : 0
              }
            }}>
            {pIndex > 0 && (
              <>
                <Divider
                  sx={{
                    display: {
                      xs: 'block',
                      sm: 'none'
                    },
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    width: '100%'
                  }}
                />
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    bottom: 0,
                    display: {
                      xs: 'none',
                      sm: 'block'
                    },
                    left: 0,
                    position: 'absolute',
                    top: 0
                  }}
                />
              </>
            )}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 500 }}>
                {xValue}
              </Typography>
              {pItem.tooltip && xHasValue && (
                <Tooltip title={pItem.tooltip} arrow>
                  <Box
                    component="span"
                    sx={{
                      alignItems: 'center',
                      display: 'inline-flex',
                      height: 16,
                      justifyContent: 'center',
                      width: 16
                    }}>
                    <Icon iconName="information" size={16} color="primary" />
                  </Box>
                </Tooltip>
              )}
            </Stack>
            <Typography variant="caption" color="textSecondary">
              {pItem.label}
            </Typography>
          </Stack>
        );
      })}
    </Box>
  );
}

/**
 * Apresenta as principais características cadastrais de um título de renda fixa.
 *
 * @param {Object} pProps Propriedades do componente.
 * @param {Object} pProps.cotacao Dados normalizados de cotação.
 * @param {Object} pProps.data Dados cadastrais do ativo.
 * @returns {JSX.Element}
 */
const RendaFixaInfoCard = memo(pProps => {
  const { cotacao = {}, data = {} } = pProps;
  const xTaxaAtual = cotacao.taxaAtual || {};
  const xTaxa = xTaxaAtual.taxa || {};
  const xIdentificacaoItems = [
    {
      id: 'serie',
      label: 'Série',
      value: data.serie,
      formatValue: pValue => pValue
    },
    {
      id: 'emissao',
      label: 'Emissão',
      value: data.emissao,
      formatValue: pvFormatDate
    },
    {
      id: 'vencimento',
      label: 'Vencimento',
      value: data.vencimento,
      formatValue: pvFormatDate
    },
    {
      id: 'dias-uteis-vencimento',
      label: 'Dias úteis até o vencimento',
      value: data.vencimento,
      formatValue: pvFormatDiasUteisAteVencimento
    }
  ];
  const xTaxaItems = [
    {
      id: 'taxa-compra',
      label: 'Taxa de compra',
      value: xTaxa.compra,
      formatValue: pvFormatTaxa
    },
    {
      id: 'taxa-venda',
      label: 'Taxa de venda',
      value: xTaxa.venda,
      formatValue: pvFormatTaxa
    },
    {
      id: 'taxa-indicativa',
      label: 'Taxa indicativa',
      value: xTaxa.indicativa,
      formatValue: pvFormatTaxa
    },
    {
      id: 'taxa-referencia',
      label: 'Referência',
      value: xTaxaAtual.data,
      formatValue: pvFormatDate,
      tooltip: 'Data de referência das taxas de compra, venda e indicativa apresentadas.'
    }
  ];

  return (
    <Card
      sx={{
        flex: 1,
        minHeight: 0
      }}>
      <Stack px={2} py={2} spacing={2} height="100%" justifyContent="space-between">
        <Stack>
          <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 500 }}>
            Ativo
          </Typography>
          <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 500 }}>
            {data.ativo || '---'}
          </Typography>
        </Stack>
        {pvRenderItems(xIdentificacaoItems, 4)}
        <Divider
          sx={{
            display: {
              xs: 'block',
              sm: 'none'
            }
          }}
        />
        {pvRenderItems(xTaxaItems)}
      </Stack>
    </Card>
  );
});

RendaFixaInfoCard.displayName = 'RendaFixaInfoCard';

export default RendaFixaInfoCard;
